import { useState } from "react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import type { InternalTaskGroup } from "@/hooks/use-internal-time-entries";

function fmtHours(seconds: number): string {
  return `${(seconds / 3600).toFixed(1).replace(".", ",")} t`;
}

/**
 * VIB-1208: én oppgave-gruppe (eller «Uten oppgave»-bucket) i den grupperte
 * InternalTimeEntriesView. Sum synlig direkte, ekspanderbart til
 * enkeltføringer. Egen fil for å holde InternalTimeEntriesView.tsx under
 * filstørrelsesgrensen.
 */
export function InternalTimeEntriesGroupRow({
  group,
  canSeeAll,
}: {
  group: InternalTaskGroup;
  canSeeAll: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-border/50 last:border-0">
      <button
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          "w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-muted/30 transition-colors",
          group.budgetLineId === null && "text-muted-foreground",
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <SrIcon
            name={expanded ? "chevron-up" : "chevron-down"}
            className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"
          />
          <span className="text-sm font-medium truncate">{group.taskName}</span>
          <span className="text-xs text-muted-foreground flex-shrink-0">
            ({group.entries.length} {group.entries.length === 1 ? "føring" : "føringer"})
          </span>
        </div>
        <span className="text-sm font-bold tabular-nums flex-shrink-0">{fmtHours(group.totalSeconds)}</span>
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/20">
              <tr className="text-xs text-muted-foreground">
                <th className="text-left p-2 pl-9 font-medium">Dato</th>
                {canSeeAll && <th className="text-left p-2 font-medium">Bruker</th>}
                <th className="text-right p-2 font-medium">Timer</th>
                <th className="text-left p-2 font-medium">Kommentar</th>
              </tr>
            </thead>
            <tbody>
              {group.entries.map((e) => (
                <tr key={e.id} className="border-t border-border/30">
                  <td className="p-2 pl-9 whitespace-nowrap">
                    {e.start_date ? formatDate(e.start_date) : "—"}
                  </td>
                  {canSeeAll && (
                    <td className="p-2 whitespace-nowrap text-muted-foreground">{e.user_name ?? "—"}</td>
                  )}
                  <td className="p-2 text-right tabular-nums">{fmtHours(e.duration_seconds)}</td>
                  <td className="p-2 text-muted-foreground">{e.comment ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
